import { lazy, Suspense, useState, useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import ShimmerSkeleton from "./components/shimmercomponents/ShimmerSkeleton";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import { Provider } from "react-redux";
import { store } from "./utils/reduxdetails/store";
import RootRedirect from "./RootDirect";
import ErrorBoundary from "./components/ErrorBoundary"; // better to import like this so that during lazyloading also can capture error
import GlobalNotification from "./components/customcomponents/GlobalNotification";
import useTenant from "./utils/hooks/useTenant";
import { TenantContext } from "./context/TenantContext";
import ChatbotLoader from "./ChatbotLoader";

// const ErrorBoundary = lazy(()=> import("./components/ErrorBoundary"))
const ErrorElement = lazy(() => import("./components/ErrorElement"));
const MainAuthComponent = lazy(() => import("./components/auth/MainAuthComponent"));
const Login = lazy(() => import("./components/auth/login/Login"));
const Signup = lazy(() => import("./components/auth/signup/Signup"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const MainPage = lazy(() => import("./components/MainPage"));
const ViewOpportunity = lazy(() => import("./components/innerPages/viewopportunity/ViewOpportunity"));
const Payment = lazy(() => import("./components/innerPages/payment/Payment"));
const ApplyOpportunity = lazy(() => import("./components/innerPages/viewopportunity/ApplyOpportunity"));
const AppliedAuditMainPage = lazy(() => import("./components/innerPages/appliedaudit/AppliedAuditMainPage"));
const CertificateQuiz = lazy(() => import("./components/innerPages/certificate/CertificateQuiz"));
const CertificateMain = lazy(()=> import("./components/innerPages/certificate/CerificateMain"))
const ProfileMainPage = lazy(() => import("./components/innerPages/profile/ProfileMainPage"));
const ReportFrontPage = lazy(() => import("./components/innerPages/reports/ReportFrontPage"));
const InnerReportPage = lazy(() => import("./components/innerPages/reports/InnerReportPage"));
const ChangePassword = lazy(()=> import("./components/auth/ChangePassword"));
const PersonalInfo = lazy(()=> import("./components/innerPages/profile/PersonalInfo"));
const NetworkStatusPopup = lazy(()=> import("./NetworkStatusPopup"));
const ShopperPolicyMain = lazy(()=> import("./components/innerPages/shopperPolicies/ShopperPolicyMain"));
const AgencyAuth = lazy(() => import("./components/agency/AgencyAuth"));
const AgencyHome = lazy(() => import("./components/agency/AgencyHome"));
const JoinAgency = lazy(() => import("./components/agency/JoinAgency"));
const AgencyReport = lazy(() => import("./components/agency/AgencyReport"));
// const CollectBot = lazy(()=>import("./components/CollectBot"))


export const routes = [
  {
    path: "/",
    // Decides at visit time (not at page load) so it stays correct after login/logout.
    element : <RootRedirect/>,
    errorElement: <ErrorElement />,
  },
  {
    path: "/auditor/auth",
    element: (
      <PublicRoute>
        <MainAuthComponent />
      </PublicRoute>
    ),
    errorElement: <ErrorElement />,
    children: [
      { path: "login", element: <Login /> },
      { path: "signup", element: <Signup /> },
      { path: "forgot_password", element: <ForgotPassword /> },
      { path: "change_password", element: <ChangePassword /> },
      { path: "join", element: <JoinAgency /> },
    ],
  },
  {
    path: "/auditor",
    element: (
      <PrivateRoute>
        <MainPage />
      </PrivateRoute>
    ),
    errorElement: <ErrorElement />,
    children: [
      { path: "audit", element: <ViewOpportunity /> },
      { path: "audit/cycle/:id", element: <ApplyOpportunity /> },
      { path: "applied_audits", element: <AppliedAuditMainPage /> },
      { path: "certification", element: <CertificateMain /> },
      { path: "certification/questions", element: <CertificateQuiz /> },
      { path: "payment", element: <Payment /> },
      { path: "shopper_policies", element: <ShopperPolicyMain/> },
      { path: "profile", element: <ProfileMainPage /> },
      { path: "report", element: <ReportFrontPage /> },
      { path: "report/:id", element: <InnerReportPage /> },
    ],
  },
  // Agencies have their own login (token "agency_token"), separate from the auditor pages.
  {
    path: "/agency/auth",
    element: <MainAuthComponent />,
    errorElement: <ErrorElement />,
    children: [
      { path: "login", element: <AgencyAuth mode="login" /> },
      { path: "signup", element: <AgencyAuth mode="signup" /> },
      { path: "forgot", element: <AgencyAuth mode="forgot" /> },
    ],
  },
  { path: "/agency", element: <AgencyHome />, errorElement: <ErrorElement /> },
  { path: "/agency/jobs/:jobId", element: <AgencyReport />, errorElement: <ErrorElement /> },
];

const appRouter = createBrowserRouter(routes);



// `router` can be overridden (e.g. with a memory router in tests).
function App({ router = appRouter }) {
  const tenant = useTenant();
  
  useEffect(() => {
    const root = document.documentElement;

    // Apply colors
    Object.entries(tenant.colors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply favicon
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) favicon.href = tenant.favicon;

    // Apply page/tab title
    document.title = "Shopper Portal" + " | " + tenant.name;

  }, [tenant]);
  
  return (
    <Provider store={store}>
      <TenantContext.Provider value={tenant}>
        {tenant.features?.chatbot && <ChatbotLoader />}
        <NetworkStatusPopup/>
        {/* <GlobalNotification/>  */} {/* If in future any kind of notification needed, can use this one */}
        <ErrorBoundary>
          <Suspense fallback={<ShimmerSkeleton />}>
            <RouterProvider router={router} />
          </Suspense>
        </ErrorBoundary>
      </TenantContext.Provider>
    </Provider>
  );
}

export default App;
